import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCalendarOff,
  IconCalendarTime,
  IconChecks,
  IconEye,
  IconFileCertificate,
  IconFilePlus,
  IconFolderCog,
  IconSend,
  IconTopologyStar3,
  IconTrash,
  IconWorldUpload,
  IconX,
} from '@tabler/icons-react';
import { type FormEvent, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import { effectiveDay, instantDay } from './DocumentDetailsPanel.js';
import classes from './Documents.module.css';
import { documentHeading, revisionBare, revisionText } from './library.js';
import { ACTION_INPUTS, DANGER_ACTIONS, publishDateBounds, workflowActions } from './office.js';
import { RevisionBadge } from './parts.js';
import type { DocumentPermissions, RevisionAction, RevisionActionInput } from './source.js';
import type { LibraryDetail, LibraryDocument, LibraryRevision, LibrarySection } from './types.js';

const ACTION_ICON: Record<RevisionAction, ReactNode> = {
  submit: <IconSend size={15} />,
  return: <IconArrowBackUp size={15} />,
  approve: <IconChecks size={15} />,
  publish: <IconWorldUpload size={15} />,
  withdraw: <IconX size={15} />,
  discard: <IconTrash size={15} />,
  reschedule: <IconCalendarTime size={15} />,
  cancel: <IconCalendarOff size={15} />,
};

/** Olayın yazılı karşılığı olan türler (onaya gönderme, iade, onay, yayın, erteleme). */
const DESCRIBED_EVENTS = new Set(['submitted', 'returned', 'approved', 'published', 'rescheduled']);

/**
 * Onay akışı şeridi (yalnız web ofis; plan M8a Task 17): açık revizyonun no'su, durum rozeti,
 * son olayı (kim, ne zaman, iade gerekçesi) ve kişinin yetkisine göre düğmeler. Müşteri
 * tasarımında yok; Form Details kartında sekmelerin üstünde, gemideki "ne değişti" şeridinin
 * yerinde durur.
 */
export function RevisionWorkflow({
  detail,
  revision,
  permissions,
  onAction,
  previewing = true,
  onPreview,
  library = 'company',
}: {
  detail: LibraryDetail;
  revision: LibraryRevision;
  permissions: DocumentPermissions;
  onAction: (action: RevisionAction) => void;
  /** Geminin eski belgesi (gemide; ADR 0081): yalnız açık kalanı kapatma (`workflowActions`). */
  library?: 'company' | 'vessel' | undefined;
  /** Önizleyicide bu revizyon mu açık; değilse "Preview Rev. 2.0" düğmesi (onaylayan inceler). */
  previewing?: boolean;
  onPreview?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.workflow.${key}`, options ?? {});
  const actions = workflowActions(revision, permissions, library);
  const last = [...revision.events].reverse().find((e) => DESCRIBED_EVENTS.has(e.event));
  const meta = last
    ? k(`events.${last.event}`, { name: last.byName ?? '—', date: instantDay(last.at) })
    : k('preparedBy', {
        name: revision.createdByName ?? '—',
        date: instantDay(revision.createdAt),
      });
  // Geminin eski belgesinde onay ve yayın artık yok: bekleyen revizyon yayınlanmaz (ADR 0081)
  const stalled =
    library === 'vessel' &&
    (revision.state === 'draft' ||
      revision.state === 'pending_approval' ||
      revision.state === 'approved');
  const waiting = stalled
    ? k('waitingVessel')
    : revision.state === 'scheduled'
      ? k('waiting.scheduled', { date: effectiveDay(revision) })
      : k(`waiting.${revision.state}`, { defaultValue: '' });
  return (
    <section className={classes.workflow} aria-label={k('title')} data-state={revision.state}>
      <span className={classes.workflowIcon} aria-hidden>
        <IconFileCertificate size={20} />
      </span>
      <div className={classes.workflowText}>
        <p className={classes.workflowTitle}>
          <span>{revisionText(revision.revisionNo)}</span>
          <RevisionBadge state={revision.state} />
          {/* Öz onay ekranda açık (ADR 0107 Ek): kimin kendi kaydını onayladığı görünür */}
          {revision.selfApproved ? (
            <Tooltip label={t('documents.library.selfApprovedHint')} withArrow multiline w={260}>
              <StatusBadge tone="info">{t('documents.library.selfApproved')}</StatusBadge>
            </Tooltip>
          ) : null}
        </p>
        <p className={classes.workflowMeta}>
          {meta}
          {last?.event === 'returned' && last.reason ? (
            <> · {k('reason', { reason: last.reason })}</>
          ) : null}
        </p>
        {waiting ? <p className={classes.workflowMeta}>{waiting}</p> : null}
      </div>
      {!previewing && onPreview ? (
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconEye size={15} />}
          onClick={onPreview}
          className={classes.workflowPreview}
        >
          {t('documents.history.preview', { revision: revisionBare(revision.revisionNo) })}
        </Button>
      ) : null}
      {actions.length > 0 ? (
        <Group gap={8} className={classes.workflowActions}>
          {actions.map((action, index) => (
            <Button
              key={action}
              size="xs"
              variant={index === 0 && !DANGER_ACTIONS.includes(action) ? 'filled' : 'default'}
              {...(DANGER_ACTIONS.includes(action) ? { color: 'red' } : {})}
              leftSection={ACTION_ICON[action]}
              onClick={() => onAction(action)}
              data-action={action}
            >
              {k(`actions.${action}`)}
            </Button>
          ))}
        </Group>
      ) : null}
      {/* Doküman başlığı ekran okuyucuya: hangi dokümanın akışı */}
      <span className={classes.srOnly}>{documentHeading(detail)}</span>
    </section>
  );
}

/**
 * İşlem penceresinin içi: ne olacağı, gerekçe (iade, geri çekme, vazgeçme), yürürlük günü (yayın)
 * ve parola (onay, yayın, geri çekme; isteğe bağlı, şirket ayarıyla zorunlu; ADR 0056). Hata
 * pencerede kalır.
 */
export function RevisionActionForm({
  action,
  detail,
  revision,
  now,
  onCancel,
  onConfirm,
}: {
  action: RevisionAction;
  detail: LibraryDetail;
  revision: LibraryRevision;
  /** Şimdiki an (ISO): yürürlük günü en erken UTC yarın, en geç 365 gün sonra (WF-5). */
  now?: string | undefined;
  onCancel: () => void;
  onConfirm: (input: Omit<RevisionActionInput, 'contentHash'>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.workflow.dialog.${key}`, options ?? {});
  const needs = ACTION_INPUTS[action];
  const [reason, setReason] = useState('');
  // Doküman revizyonunda parola isteğe bağlı (ADR 0056 §1, §8): alan yerine bilgi metni; şirket
  // ayarı açıksa ya da sunucu isterse alan görünür
  const reauth = useReauth(
    detail.owner === 'vessel' ? 'vessel_document_revision' : 'document_revision',
  );
  const [effectiveOn, setEffectiveOn] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Yeniden zamanlamada yeni gün zorunlu (yayında boş gün "hemen"dir)
  const ready =
    (!needs.reason || reason.trim() !== '') &&
    (!needs.password || !reauth.missing) &&
    (action !== 'reschedule' || effectiveOn !== null);
  // Sınır pencere açılırken alınır: ekranın "now"u ağacın son okunduğu an olabilir (M8a3-R17)
  const [openedAt] = useState(() => now ?? new Date().toISOString());
  const bounds = publishDateBounds(openedAt);
  // Yürürlükteki revizyon geri çekilirse kapsamdaki gemiler yürürlükte revizyonsuz kalır
  // (ADR 0028 #26 §5; inceleme WF-10)
  const orphaned =
    action === 'withdraw' && revision.id === detail.current?.revisionId
      ? (detail.distribution?.filter((cell) => cell.inScope).length ?? null)
      : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        ...(needs.reason ? { reason: reason.trim() } : {}),
        ...(needs.password ? reauth.body() : {}),
        ...(needs.effectiveOn ? { effectiveOn } : {}),
      });
    } catch (failure) {
      const code = (failure as { code?: string }).code ?? 'GENERIC';
      setError(t(`documents.errors.${code}`, { defaultValue: t('documents.errors.GENERIC') }));
      reauth.noteError(failure);
      reauth.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        <Text size="sm">
          {k(`bodies.${action}`, {
            document: documentHeading(detail),
            revision: revisionBare(revision.revisionNo),
          })}
        </Text>
        {action === 'withdraw' && revision.id === detail.current?.revisionId ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {orphaned === null ? k('withdrawWarning') : k('withdrawVessels', { count: orphaned })}
          </Alert>
        ) : null}
        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
            {error}
          </Alert>
        ) : null}
        {needs.reason ? (
          <Textarea
            label={k('reason')}
            description={k('reasonHint')}
            required
            autosize
            minRows={3}
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        ) : null}
        {needs.effectiveOn ? (
          <DayInput
            label={k('effectiveOn')}
            description={k('effectiveHint', {
              min: formatDay(bounds.min),
              max: formatDay(bounds.max),
            })}
            value={effectiveOn}
            onChange={setEffectiveOn}
            minDate={bounds.min}
            maxDate={bounds.max}
            clearable
          />
        ) : null}
        {needs.password ? (
          <ReauthField
            reauth={reauth}
            label={k('password')}
            description={k('passwordHint')}
            mb="0"
          />
        ) : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            {k('cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={!ready}
            {...(DANGER_ACTIONS.includes(action) ? { color: 'red' } : {})}
            leftSection={ACTION_ICON[action]}
          >
            {t(`documents.workflow.actions.${action}`)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export function RevisionActionDialog({
  action,
  detail,
  revision,
  now,
  onClose,
  onConfirm,
}: {
  action: RevisionAction | null;
  detail: LibraryDetail;
  revision: LibraryRevision;
  now?: string | undefined;
  onClose: () => void;
  onConfirm: (
    action: RevisionAction,
    input: Omit<RevisionActionInput, 'contentHash'>,
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={action !== null}
      onClose={onClose}
      title={action ? t(`documents.workflow.dialog.titles.${action}`) : ''}
      closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
      centered
    >
      {action ? (
        <RevisionActionForm
          key={`${action}-${revision.id}`}
          action={action}
          detail={detail}
          revision={revision}
          now={now}
          onCancel={onClose}
          onConfirm={(input) => onConfirm(action, input)}
        />
      ) : null}
    </Modal>
  );
}

export interface PendingApprovalItem {
  section: LibrarySection;
  document: LibraryDocument;
}

/**
 * Panoda onaylayanın uyarıları (mimari belge §7.3): onay ya da yayın bekleyen revizyonlar.
 * Satır dokümanı Form List'te açar.
 */
export function PendingApprovalsList({
  items,
  onOpen,
}: {
  items: readonly PendingApprovalItem[];
  onOpen: (item: PendingApprovalItem) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section className={classes.bell} aria-label={t('documents.pending.title')}>
      <p className={classes.bellTitle}>{t('documents.pending.title')}</p>
      <ul className={classes.bellList}>
        {items.map((item) => {
          const latest = item.document.latest;
          if (!latest) return null;
          const key =
            latest.state === 'approved'
              ? 'documents.pending.awaitsPublishing'
              : 'documents.pending.awaitsApproval';
          return (
            <li key={item.document.id}>
              <UnstyledButton className={classes.bellItem} onClick={() => onOpen(item)}>
                <span className={classes.bellIcon} data-tone="warning" aria-hidden>
                  <IconFileCertificate size={16} />
                </span>
                <span>
                  {t(key, {
                    document: documentHeading(item.document),
                    revision: revisionBare(latest.revisionNo),
                  })}
                </span>
              </UnstyledButton>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Form List ve Manuals'ın sayfa başlığındaki ofis düğmeleri (plan M8a Task 17): Distribution
 * (dağıtım panosu; menü öğesi değildir, ADR 0019) ve New Document (`documents.create`). Başlık
 * kartı müşteri tasarımındaki gibi Edit, Download ve Print'le kalır.
 */
export function OfficeHeaderActions({
  permissions,
  onNew,
  onDistribution,
  onManageFolders,
  newHint,
}: {
  permissions: DocumentPermissions;
  onNew: () => void;
  /** Dağıtım panosu; PMS Instructions'ta yok. */
  onDistribution?: () => void;
  /**
   * Manuals ▸ "Manage folders" (ADR 0038 §8): menü öğesi değil, breadcrumb satırında düğme
   * (`menu-yapisi.md` değişmez). ISM'de klasörler Form Categories menüsündedir.
   */
  onManageFolders?: (() => void) | undefined;
  /** New Document'ın ipucu (gemide ISM: formu doldurmak için Fill in; 2026-10-16). */
  newHint?: string | undefined;
}) {
  const { t } = useTranslation();
  const hintId = useId();
  const newButton = (
    <Button
      size="xs"
      leftSection={<IconFilePlus size={15} />}
      onClick={onNew}
      aria-describedby={newHint ? hintId : undefined}
    >
      {t('documents.head.newDocument')}
    </Button>
  );
  return (
    <>
      {onManageFolders ? (
        <Button
          variant="default"
          size="xs"
          leftSection={<IconFolderCog size={15} />}
          onClick={onManageFolders}
        >
          {t('documents.folders.manage')}
        </Button>
      ) : null}
      {onDistribution ? (
        <Button
          variant="default"
          size="xs"
          leftSection={<IconTopologyStar3 size={15} />}
          onClick={onDistribution}
        >
          {t('documents.head.distribution')}
        </Button>
      ) : null}
      {permissions.create && newHint ? (
        <>
          <Tooltip label={newHint} withArrow multiline w={280}>
            {newButton}
          </Tooltip>
          <span id={hintId} className={classes.srOnly}>
            {newHint}
          </span>
        </>
      ) : permissions.create ? (
        newButton
      ) : null}
    </>
  );
}
